<template>
  <div class="ppa-optional-upload">
    <DocumentsUploadSection
      v-model="actionInstructions"
      :companyId="companyId"
      @analysis-complete="handleAnalysisComplete"
    />
    <div v-if="!hasUploadedFiles" class="ppa-optional-upload__divider">
      <span class="ppa-optional-upload__divider-line" />
      <span class="ppa-optional-upload__divider-text">
        {{ $t("production_strategy.or_skip_and_continue") }}
      </span>
      <span class="ppa-optional-upload__divider-line" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DocumentsUploadSection from "@/dashboard/production_strategy/overview/PPA/components/DocumentsUploadSection.vue";
import type { ActionInstructions, PortfolioInstructionsPatch } from "@/types";
import { computed } from "vue";

defineProps<{
  companyId: number;
}>();

const actionInstructions = defineModel<ActionInstructions>({ required: true });

const emit = defineEmits<{
  "analysis-complete": [
    analysisResult: unknown,
    portfolioInstructionsPatch?: PortfolioInstructionsPatch,
  ];
}>();

const hasUploadedFiles = computed(
  () => (actionInstructions.value.custom_user_upload_ids ?? []).length > 0
);

const handleAnalysisComplete = (
  analysisResult: unknown,
  portfolioInstructionsPatch?: PortfolioInstructionsPatch
) => {
  emit("analysis-complete", analysisResult, portfolioInstructionsPatch);
};
</script>

<style scoped lang="scss">
.ppa-optional-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ppa-optional-upload__divider {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: var(--phoenix-gray-500);
}

.ppa-optional-upload__divider-line {
  flex: 1 1 auto;
  height: 1px;
  background: var(--phoenix-border-color-translucent);
}

.ppa-optional-upload__divider-text {
  white-space: nowrap;
}

// Reskin the shared UploadChecklistItemComponent dropzone to match the PPA
// wizard design: rounded icon tile, tighter type, blue-tinted hover state.
.ppa-optional-upload :deep(.dropzone) {
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  min-height: 0;
  margin-bottom: 0;
  border: 1.5px dashed var(--phoenix-border-color);
  border-radius: 12px;
  background: var(--phoenix-gray-50);
  color: var(--phoenix-gray-900);
  transition: all 0.15s ease;
}

.ppa-optional-upload :deep(.dropzone:hover) {
  background: rgba(var(--phoenix-primary-rgb), 0.03);
  border-color: var(--phoenix-primary-border-subtle, var(--phoenix-primary));
  color: var(--phoenix-gray-900);
}

.ppa-optional-upload :deep(.dropzone .dropzone-content) {
  display: flex;
  align-items: center;
}

.ppa-optional-upload :deep(.dropzone .upload-icon) {
  width: 44px;
  height: 44px;
  padding: 0;
  margin-right: 12px;
  border-radius: 12px;
  background: var(--phoenix-primary-bg-subtle);
  color: var(--phoenix-primary);
  display: grid;
  place-items: center;
  font-size: 18px;
}

.ppa-optional-upload :deep(.dropzone p) {
  font-weight: 700;
  font-size: 14px;
  color: var(--phoenix-gray-1000);
  line-height: 1.25;
}

.ppa-optional-upload :deep(.dropzone small) {
  display: block;
  font-size: 12px;
  color: var(--phoenix-gray-500);
  margin-top: 2px;
  opacity: 1;
}
</style>
